import React, { lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvite } from './pages/AcceptInvite';
// Every page is its own chunk: the first screen downloads only the shell and
// the page it shows; the rest load when visited.
const page = (load, name) => lazy(() => load().then((m) => ({ default: name ? m[name] : m.default })));
const Dashboard = page(() => import('./pages/Dashboard'), 'Dashboard');
const Orders = page(() => import('./pages/Orders'), 'Orders');
const OrderDetail = page(() => import('./pages/OrderDetail'), 'OrderDetail');
const NewOrder = page(() => import('./pages/NewOrder'), 'NewOrder');
const WhatsAppCRM = page(() => import('./pages/WhatsAppCRM'), 'WhatsAppCRM');
const Tasks = page(() => import('./pages/Tasks'), 'Tasks');
const InventoryHub = page(() => import('./pages/InventoryHub'), 'InventoryHub');
const Analytics = page(() => import('./pages/Analytics'), 'Analytics');
const AdvancedAnalytics = page(() => import('./pages/AdvancedAnalytics'), 'AdvancedAnalytics');
const ImportWizard = page(() => import('./pages/ImportWizard'), 'ImportWizard');
const HistoricalImport = page(() => import('./pages/HistoricalImport'), 'HistoricalImport');
const MasterSKU = page(() => import('./pages/MasterSKU'), 'MasterSKU');
const Returns = page(() => import('./pages/Returns'), 'Returns');
const ReturnDetail = page(() => import('./pages/ReturnDetail'), 'ReturnDetail');
const Replacements = page(() => import('./pages/Replacements'), 'Replacements');
const Installations = page(() => import('./pages/Installations'));
const ReplacementDetail = page(() => import('./pages/ReplacementDetail'), 'ReplacementDetail');
const Claims = page(() => import('./pages/Claims'), 'Claims');
const WebsiteClaims = page(() => import('./pages/WebsiteClaims'));
const CareAlerts = page(() => import('./pages/CareAlerts'), 'CareAlerts');
const Reports = page(() => import('./pages/Reports'), 'Reports');
const Channels = page(() => import('./pages/Channels'), 'Channels');
const Settings = page(() => import('./pages/Settings'), 'Settings');
const Team = page(() => import('./pages/Team'), 'Team');
const CancelledOrders = page(() => import('./pages/CancelledOrders'));
const ResolvedOrders = page(() => import('./pages/ResolvedOrders'));
const AbandonedCarts = page(() => import('./pages/AbandonedCarts'));
const AmazonInbox = page(() => import('./pages/AmazonInbox'));
const Warehouses = page(() => import('./pages/Warehouses'), 'Warehouses');
const LogisticsReadiness = page(() => import('./pages/LogisticsReadiness'), 'LogisticsReadiness');
const Logistics = page(() => import('./pages/Logistics'), 'Logistics');
const TrackingDashboard = page(() => import('./pages/TrackingDashboard'), 'TrackingDashboard');
const Notifications = page(() => import('./pages/Notifications'), 'Notifications');
import { Layout } from './components/Layout';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" /> : children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/accept-invite" element={<PublicRoute><AcceptInvite /></PublicRoute>} />
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="orders" element={<Orders />} />
            <Route path="orders/new" element={<NewOrder />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="orders/import" element={<ImportWizard />} />
            <Route path="orders/import-historical" element={<HistoricalImport />} />
            <Route path="cancelled-orders" element={<CancelledOrders />} />
            <Route path="resolved-orders" element={<ResolvedOrders />} />
            <Route path="abandoned-carts" element={<AbandonedCarts />} />
            <Route path="amazon-inbox" element={<AmazonInbox />} />
            <Route path="master-sku" element={<MasterSKU />} />
            <Route path="returns" element={<Returns />} />
            <Route path="returns/:id" element={<ReturnDetail />} />
            <Route path="replacements" element={<Replacements />} />
            <Route path="replacements/:id" element={<ReplacementDetail />} />
            <Route path="installations" element={<Installations />} />
            <Route path="claims" element={<Claims />} />
            <Route path="website-claims" element={<WebsiteClaims />} />
            <Route path="care-alerts" element={<CareAlerts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="channels" element={<Channels />} />
            <Route path="warehouses" element={<Warehouses />} />

            <Route path="logistics-readiness" element={<LogisticsReadiness />} />
            <Route path="logistics" element={<Logistics />} />
            <Route path="logistics/tracking" element={<TrackingDashboard />} />
            <Route path="settings" element={<Settings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="whatsapp" element={<WhatsAppCRM />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="inventory" element={<InventoryHub />} />
            <Route path="inventory/intelligence" element={<Navigate to="/inventory" replace />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="analytics/advanced" element={<AdvancedAnalytics />} />
            <Route path="team" element={<Team />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
